/*
 * New project, Edit project and Archive project: pure model, no React and nothing that touches `window`. Per-field
 * validation, what each field shows, the request bodies and the archive wording.
 */
import { DEFAULT_PRODUCTION_BRANCH, branchPatternProblem, type Project } from '@hangar/shared';
import type { ProjectCreateInput, ProjectPatchInput } from '@/api/endpoints';
import { applyName, applySlug } from '@/lib/formModel';
import { SLUG_HINT, SLUG_RE } from '@/lib/format';

const nameProblem = (name: string) => (!name.trim() ? 'Name is required' : name.length > 120 ? 'Keep the name under 120 characters' : undefined);

const descriptionProblem = (description: string) => (description.length > 2000 ? 'Keep the description under 2000 characters' : undefined);

/* ---------- New project ---------- */

export interface NewProjectValues {
  name: string;
  slug: string;
  /** Once typed by hand the slug stops following the name. */
  slugTouched: boolean;
  description: string;
  /** Create the staging, preprod and production environments with the project. */
  defaults: boolean;
  /** The production branch ('' = the default). */
  branch: string;
}

export type NewProjectField = 'slug' | 'name' | 'description' | 'productionBranch';

export const INITIAL_NEW_PROJECT: NewProjectValues = { name: '', slug: '', slugTouched: false, description: '', defaults: true, branch: '' };

/** The branch only matters when the default environments are created; empty means the default branch. */
const branchProblem = (defaults: boolean, branch: string) => {
  if (!defaults || !branch.trim()) return undefined;
  return branch.includes('*') ? 'One branch name, without *' : branchPatternProblem(branch.trim());
};

export const validateNewProject = (v: NewProjectValues): Record<NewProjectField, string | undefined> => ({
  slug: !v.slug ? 'Slug is required' : !SLUG_RE.test(v.slug) ? SLUG_HINT : undefined,
  name: nameProblem(v.name),
  description: descriptionProblem(v.description),
  productionBranch: branchProblem(v.defaults, v.branch),
});

/** One text field changed to `raw` (the name feeds the slug until the slug is typed). */
export function applyNewProjectChange(v: NewProjectValues, field: NewProjectField, raw: string): NewProjectValues {
  if (field === 'name') return applyName(v, raw);
  if (field === 'slug') return applySlug(v, raw);
  return field === 'description' ? { ...v, description: raw } : { ...v, branch: raw };
}

/**
 * What each field shows: the API's message (an emptied one stays empty), else the local one after submit; the slug also
 * while typing, the branch as soon as something is typed.
 */
export function newProjectErrors(local: Record<NewProjectField, string | undefined>, serverFields: Record<string, string>, submitted: boolean, v: NewProjectValues): Record<NewProjectField, string | undefined> {
  const at = (field: NewProjectField, live = false) => serverFields[field] ?? (submitted || live ? local[field] : undefined);
  return {
    slug: at('slug', v.slug.length > 0),
    name: at('name'),
    description: at('description'),
    productionBranch: at('productionBranch', Boolean(v.branch.trim())),
  };
}

/** Where the focus goes when validation fails: the branch only when it alone is wrong, else the slug, else the name. */
export function invalidFocusTarget(local: Record<NewProjectField, string | undefined>): 'productionBranch' | 'slug' | 'name' | undefined {
  if (!Object.values(local).some(Boolean)) return undefined;
  if (local.productionBranch && !local.slug && !local.name) return 'productionBranch';
  return local.slug ? 'slug' : 'name';
}

/** `POST /projects`: the description and the branch only when filled in (the branch only with the default environments). */
export function buildNewProjectBody(v: NewProjectValues): ProjectCreateInput {
  return {
    slug: v.slug,
    name: v.name.trim(),
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
    createDefaultEnvironments: v.defaults,
    ...(v.defaults && v.branch.trim() ? { productionBranch: v.branch.trim() } : {}),
  };
}

/** The toast under "Project … created": the environments and the branch production builds. */
export function createdDescription(v: Pick<NewProjectValues, 'defaults' | 'branch'>): string | undefined {
  const accepted = v.branch.trim() || DEFAULT_PRODUCTION_BRANCH;
  return v.defaults ? `staging · preprod · production (production builds ${accepted} only)` : undefined;
}

/* ---------- Edit project ---------- */

export interface EditProjectValues {
  name: string;
  description: string;
}

export type EditProjectField = keyof EditProjectValues;

export const editProjectValuesOf = (p: Pick<Project, 'name' | 'description'>): EditProjectValues => ({ name: p.name, description: p.description ?? '' });

export const validateEditProject = (v: EditProjectValues): Record<EditProjectField, string | undefined> => ({
  name: nameProblem(v.name),
  description: descriptionProblem(v.description),
});

/** `PATCH /projects/{id}`: only the changed fields; an empty description clears it (null). */
export function buildEditProjectBody(project: Pick<Project, 'name' | 'description'>, v: EditProjectValues): ProjectPatchInput {
  const nextDescription = v.description.trim() || null;
  return {
    ...(v.name.trim() !== project.name ? { name: v.name.trim() } : {}),
    ...(nextDescription !== (project.description ?? null) ? { description: nextDescription } : {}),
  };
}

/* ---------- Archive project ---------- */

/** The sum of the counts, or `undefined` while any of them still loads. */
export const sumCounts = (parts: Array<number | undefined>) => (parts.some((n) => n === undefined) ? undefined : parts.reduce<number>((a, n) => a + (n ?? 0), 0));

const appWord = (n: number) => (n === 1 ? 'app' : 'apps');

export function archiveConfirmLabel(withApps: boolean, appCount: number | undefined): string {
  if (!withApps || !appCount) return 'Archive project';
  return `Archive project and ${appWord(appCount)}`;
}

/** The toast line saying the apps went with the project (nothing when they did not, or there are none). */
export function archivedToastDescription(withApps: boolean, appCount: number | undefined): string | undefined {
  if (!withApps || !appCount) return undefined;
  return `${appCount} ${appWord(appCount)} archived with it.`;
}

/** "Archive its app too" / "Archive its 3 apps too". */
export const archiveAppsOption = (appCount: number) => `Archive its ${appCount === 1 ? 'app' : `${appCount} apps`} too`;
